unit uLockerState;

// This is for tracking the Locker's current state.
// It was initially a record, but I changed it to a class to make it easier to work with.
// So all of the fields are public and the only two methods are Serialize and Deserialize
// to make it easy to save and load to/from local storage as it simply wants a string argument.
//
// This is like a cookie where the main unit saves it to local browser storage and then
// tells the browser to navigate to the same URL (essentially a F5 refresh) and then reads
// this data back to figure out what to do. I think it's quite ingenious.
// In order to work, the SAME KEY NEEDS TO BE USED across multiple refreshes since the
// app's state is wiped out on each refresh. So the key is called a SessionKey.
//
// If no SessionKey is passed in, then it generates one dynamically as a GUID, but this
// isn't useful through a refresh since you don't have access to the key.
// Maybe using a phone# or email would be more meaningful for a given user,
// or even their UserID, but it's already there.
//
// In the future, I'm looking at workflows that can spawn off multiple tasks, and the Workflow
// Manager would assign SessionKeys for each thread, although the work would all be in the
// background so there wouldn't be any screen refreshes happening.

interface

uses
  System.SysUtils, System.Classes, JS, Web, Weblib.JSON, WEBLib.Storage;

type

  TRunMode  = ( rmOneStep, rmAllowRepeats, rmSerial, rmBatch, rmFanOut );
  TRunStage = ( rsStage1, rsStage2, rsSerial );  // likely to change

  TLockerState = class
  private
    FSessionKey : string;

  public
    UserID   : string;
    LockerID : string;
    AutoRun  : Boolean;
    RunMode  : TRunMode;
    RunStage : TRunStage;

  private
    // users don't need to see these, just the Save and Get methods
    function Serialize : string;
    class function Deserialize( const str : string ) : TLockerState; static;

  public
    // The SessionKey is used to identify this locker's state data in local storage
    // across a session. It needs to persist to keep the data available between
    // app refreshes.
    // Each instance is tied to the given key, and each instance
    // should have a different key
    // If a storage object exists for aKey, it is loaded in.
    constructor Create(const aSessionKey: string='');

    // this is useful to get dynamically-generated keys. (they're GUIDs)
    property SessionKey : string read FSessionKey;

    // This is True iff UserID and LockerID are both not empty
    function IsValid : boolean;

    procedure SaveToLocalStorage;
    function  GetFromLocalStorage: Boolean;

    // ClearFromLocalStorage returns True if the key was found and removed
    function RemoveFromLocalStorage : Boolean;


  end;

implementation

{ TLockerState }

//===============================================================
constructor TLockerState.Create(const aSessionKey: string='');
//===============================================================
begin
  if (aSessionKey <> '') then
  begin
    FSessionKey := aSessionKey;
    GetFromLocalStorage;
  end
  else
  begin
    FSessionKey := TGUID.NewGuid.ToString;
    UserID := '';
    LockerID := '';
    AutoRun := False;
    RunMode := rmOneStep;
    RunStage := rsStage1;
  end;
end;

//===============================================================
function TLockerState.IsValid : boolean;
//===============================================================
begin
  Result := (UserID <> '') and (LockerID <> '');
end;

//===============================================================
function TLockerState.Serialize : string;
//===============================================================
// use something like this to save to local storage:

begin
  Result := TJSJSON.stringify(Self);
end;

//===============================================================
class function TLockerState.Deserialize(const str: string) : TLockerState;
//===============================================================
var
  JSObj: JSValue;
begin
  JSObj := TJSJSON.parse(str);
  Result := TLockerState(JSObj);
end;

//===============================================================
procedure TLockerState.SaveToLocalStorage;
//===============================================================
begin
  TLocalStorage.SetValue(FSessionKey, Serialize);
end;

//===============================================================
function TLockerState.GetFromLocalStorage : Boolean;
//===============================================================
var
  s: string;
  JSObj: JSValue;
begin
  Result := False;
  s := TLocalStorage.GetValue(FSessionKey);
  if (s <> '') then
  begin
    JSObj := TJSJSON.parse(s);
    TJSObject.assign(TJSObject(Self), TJSObject(JSObj));
    Result := True;
  end;
end;

//===============================================================
function TLockerState.RemoveFromLocalStorage : Boolean;
//===============================================================
begin
   if (TLocalStorage.GetValue(FSessionKey) <> '') then
    begin
      TLocalStorage.RemoveKey(FSessionKey); // this has to be removed explicity
    end;
end;

end.
